import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  FileLinesIcon,
  GiftIcon,
  PhotoFilmIcon,
  CircleDollarIcon,
  SackDollarIcon,
  UserPlusIcon,
  UserGroupIcon,
  TriangleExclamationIcon
} from '@revfluence/fresh-icons/solid/esm';
import { cn } from '@frontend/shadcn/lib/utils';
import { useGetCountsForProjectQuery, useGetUsersQuery, useProjectByIdQuery } from '@frontend/app/hooks';
import { ApolloError } from '@apollo/client';
import { useGetSummaryToolbarStats } from '@frontend/app/hooks/useGetSummaryToolbarStats';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { SOCIAL_DISCOVERY_APP_ID } from '@frontend/app/constants/applicationIds';
import { Button } from '@/shadcn/components/ui/button';
import { Avatar, AvatarFallback } from '@/shadcn/components/ui/avatar';
import { Skeleton } from '@/shadcn/components/ui/skeleton';
import { WorkletSpecKey } from '../../utils';

interface StatisticItemProps {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  className?: string;
}

const StatisticItem: React.FC<StatisticItemProps> = ({ icon, value, label, className }) => (
  <div className={cn('flex items-center gap-2', className)}>
    <div className="flex items-center justify-center text-grey-5">
      {icon}
    </div>
    <div className="flex items-center gap-1">
      <span className="text-grey-6 text-sm font-medium">{value}</span>
      <span className="text-grey-5 text-sm font-normal">{label}</span>
    </div>
  </div>
);

interface TotalMembersProps {
  programId: number;
}

const TotalMembers = ({ programId }: TotalMembersProps) => {
  const { data, loading, error } = useGetCountsForProjectQuery({
    variables: {
      projectId: programId,
    },
    fetchPolicy: 'network-only',
  });

  if (loading) {
    return <Skeleton className="w-40 h-6" />;
  }

  if (error) {
    return (
      <StatisticItem
        icon={<TriangleExclamationIcon className="text-danger" />}
        value={0}
        label="Total Members (Error)"
      />
    );
  }

  return (
    <StatisticItem
      icon={<UserGroupIcon />}
      value={data?.counts?.all_in_progress ?? 0}
      label="Total Members"
    />
  );
};

export const Metric = ({
  icon,
  value,
  label,
  loading,
  error,
  className,
}: {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  loading: boolean;
  error?: ApolloError;
  className?: string;
}) => {
  if (loading) {
    return <Skeleton className={cn('w-40 h-6', className)} />;
  }

  if (error) {
    return <StatisticItem icon={<TriangleExclamationIcon className="text-danger" />} value={0} label={label} className={className} />;
  }

  return (
    <StatisticItem
      icon={icon}
      value={value}
      label={label}
      className={className}
    />
  );
};

export const ProjectOwner = ({ ownerId }: { ownerId: string }) => {
  const { data, loading } = useGetUsersQuery();

  const owner = data?.users?.find((user) => user.id === ownerId);

  if (loading) {
    return <Skeleton className="w-8 h-8 rounded-full" />;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Avatar className="w-8 h-8 border-2 border-white">
            <AvatarFallback className="bg-blue-3 text-grey-6 text-xs font-medium">
              {owner?.name ? owner.name.split(' ').map((n) => n[0]).join('') : '—'}
            </AvatarFallback>
          </Avatar>
        </TooltipTrigger>
        <TooltipContent>
          Project Owner: {owner?.name ?? '—'}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const SummaryHeader: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const history = useHistory();

  const { summaryToolbarStats, isSummaryToolbarStatsLoading, summaryToolbarStatsError } = useGetSummaryToolbarStats({
    variables: {
      programId: Number(projectId),
    },
    fetchPolicy: 'network-only',
  });

  const { data: projectData, loading: isProjectLoading, error: projectError } = useProjectByIdQuery({
    variables: {
      id: Number(projectId),
    },
    fetchPolicy: 'network-only',
  });

  const { signedBriefs, shippedOrders, approvedContent, totalRevenue } = summaryToolbarStats ?? {};

  const showBriefMetrics = !projectData || projectData?.project?.workletSpecKeys?.includes(WorkletSpecKey.SendTerms);
  const showOrderMetrics = !projectData || [WorkletSpecKey.PFACreatorProductSelection, WorkletSpecKey.PFASendProduct, WorkletSpecKey.PFAOfflineCreatorProductSelection, WorkletSpecKey.PFACreatorProductSelectionV2].some((key) => projectData?.project?.workletSpecKeys?.includes(key));
  const showContentMetrics = !projectData || projectData?.project?.workletSpecKeys?.includes(WorkletSpecKey.ReviewContent);
  const showRevenueMetrics = !projectData || [WorkletSpecKey.STASalesLinks, WorkletSpecKey.STASalesLinksAndPromoCodes, WorkletSpecKey.STAPromoCodes].some((key) => projectData?.project?.workletSpecKeys?.includes(key));

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  return (
    <div className="w-full py-2 bg-white border-b border-grey-2 flex flex-col">
      <div className="px-8 flex items-center justify-between gap-6 flex-wrap">
        <div className="flex-1 flex items-center gap-6 flex-wrap">
          <TotalMembers programId={Number(projectId)} />
          {showBriefMetrics && <Metric
            icon={<FileLinesIcon />}
            value={signedBriefs}
            label="Signed Briefs"
            loading={isSummaryToolbarStatsLoading}
            error={summaryToolbarStatsError}
          />}
          {showOrderMetrics && <Metric
            icon={<GiftIcon />}
            value={shippedOrders}
            label="Orders Shipped"
            loading={isSummaryToolbarStatsLoading}
            error={summaryToolbarStatsError}
          />}
          {showContentMetrics && <Metric
            icon={<PhotoFilmIcon />}
            value={approvedContent}
            label="Content Created"
            loading={isSummaryToolbarStatsLoading}
            error={summaryToolbarStatsError}
          />}
          {showRevenueMetrics && <Metric
            icon={<CircleDollarIcon />}
            value={currencyFormatter.format(totalRevenue ?? 0)}
            label="Affiliate Revenue"
            loading={isSummaryToolbarStatsLoading}
            error={summaryToolbarStatsError}
          />}
        </div>

        <div className="flex items-center gap-2">
          <Metric
            icon={<SackDollarIcon />}
            value={currencyFormatter.format(projectData?.project?.programMetadata?.budget ?? 0)}
            label="Campaign Budget"
            loading={isProjectLoading}
            error={projectError}
          />

          <ProjectOwner ownerId={projectData?.project?.owner} />

          <div className="flex items-center gap-2 ml-2">
            <Button
              variant="primary"
              size="sm"
              className="gap-2"
              onClick={() => {
                const currentPath = history.location.pathname;
                history.push({
                  pathname: `/app/${SOCIAL_DISCOVERY_APP_ID}`,
                  state: { from: currentPath }
                });
              }}
            >
              <UserPlusIcon className="w-3.5 h-3.5" />
              Invite
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
